import { Search, Plus, Eye, Clock, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function VendorProductsTab({
  vendors, selectedVendor, setSelectedVendor, searchTerm, setSearchTerm,
  message, getFilteredProducts, getStatusBadgeClass,
  showAddForm, setShowAddForm, newProduct, setNewProduct,
  handleAddProduct, productSortField, productSortDirection, handleProductSort, loading,
}) {
  const allProductNames = [...new Set(vendors.flatMap(v => (v.products || []).map(p => p.productName)).filter(Boolean))];
  const allBrands = [...new Set(vendors.flatMap(v => (v.products || []).map(p => p.brand)).filter(Boolean))];

  const getSortIcon = (field) => {
    if (field !== productSortField) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    return productSortDirection === "asc"
      ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
      : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />;
  };

  const filteredProducts = getFilteredProducts();

  return (
    <>
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <CardTitle className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <span className="text-[#0263cc] text-xl font-semibold">Product Management</span>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative w-full sm:w-auto">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                />
              </div>
              <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                <SelectTrigger className="w-full sm:w-auto border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.filter((v) => v.status === "active").map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id.toString()}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setShowAddForm(true)} className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white rounded-lg flex items-center gap-2">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Product</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {message.content && (
            <Alert
              variant={message.type === "error" ? "destructive" : "default"}
              className={`m-4 rounded-lg ${message.type === "success" ? "bg-green-50 text-green-800 border-green-200" : message.type === "error" ? "bg-red-50 text-red-800 border-red-200" : "bg-blue-50 text-blue-800 border-blue-200"}`}
            >
              <AlertDescription className="font-medium">{message.content}</AlertDescription>
            </Alert>
          )}

          {/* Mobile Cards */}
          <div className="block md:hidden space-y-4 p-4">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? "No products found matching your search" : "No products available"}
              </div>
            ) : (
              filteredProducts.map((product, index) => (
                <div key={index} className="border rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium">{product.productName}</h3>
                    <span className={getStatusBadgeClass(product.paymentStatus)}>
                      {product.paymentStatus === "paid" ? "Paid" : product.paymentStatus === "partial" ? "Partially Paid" : "Pending"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div className="text-gray-500">Vendor:</div><div>{product.vendorName}</div>
                    <div className="text-gray-500">Brand:</div><div>{product.brand}</div>
                    <div className="text-gray-500">Quantity:</div><div>{product.quantity}</div>
                    <div className="text-gray-500">Cost:</div><div>₹{product.costPrice}</div>
                    <div className="text-gray-500">MRP:</div><div>₹{product.mrp}</div>
                    <div className="text-gray-500">Buy Date:</div><div>{new Date(product.buyDate).toLocaleDateString()}</div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button variant="ghost" size="sm" className="text-[#0263cc] hover:text-[#0263cc]/80 p-1">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <TableHeader>
                <TableRow className="bg-gray-50 border-y border-gray-200">
                  {[
                    { key: "productName", label: "Product Name" },
                    { key: "brand", label: "Brand" },
                    { key: "quantity", label: "Quantity" },
                    { key: "mrp", label: "MRP" },
                    { key: "costPrice", label: "Cost Price" },
                    { key: "buyDate", label: "Buy Date" },
                    { key: "paymentStatus", label: "Payment Status" },
                  ].map(({ key, label }) => (
                    <TableHead key={key} className="font-semibold text-gray-600 cursor-pointer" onClick={() => handleProductSort(key)}>
                      <div className="flex items-center">{label}{getSortIcon(key)}</div>
                    </TableHead>
                  ))}
                  <TableHead className="font-semibold text-gray-600">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <tbody className="divide-y divide-gray-200">
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      {searchTerm ? "No products found matching your search" : "No products available"}
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 text-sm text-gray-600">{product.productName}</td>
                      <td className="px-4 py-4 font-medium">{product.brand}</td>
                      <td className="px-4 py-4">{product.quantity}</td>
                      <td className="px-4 py-4">₹{product.mrp}</td>
                      <td className="px-4 py-4">₹{product.costPrice}</td>
                      <td className="px-4 py-4">{new Date(product.buyDate).toLocaleDateString()}</td>
                      <td className="px-4 py-4">
                        <span className={getStatusBadgeClass(product.paymentStatus)}>
                          {product.paymentStatus === "paid" ? "Paid" : product.paymentStatus === "partial" ? "Partially Paid" : "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Button variant="ghost" size="sm" className="text-[#0263cc] hover:text-[#0263cc]/80 p-1">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              {filteredProducts.length} {filteredProducts.length === 1 ? "product" : "products"} displayed
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Product Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add New Product</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <Label htmlFor="vendor-select">Select Vendor</Label>
              <Select value={selectedVendor} onValueChange={(value) => { setSelectedVendor(value); setNewProduct({ ...newProduct, vendorId: value }); }}>
                <SelectTrigger id="vendor-select" className="w-full"><SelectValue placeholder="Select Vendor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Select Vendor</SelectItem>
                  {vendors.filter((v) => v.status === "active").map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id.toString()}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="product-name">Item</Label>
              <Select value={newProduct.productName} onValueChange={(value) => setNewProduct({ ...newProduct, productName: value })}>
                <SelectTrigger id="product-name"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  {allProductNames.map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="brand">Brand</Label>
              <Select value={newProduct.brand} onValueChange={(value) => setNewProduct({ ...newProduct, brand: value })}>
                <SelectTrigger id="brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
                <SelectContent>
                  {allBrands.map(brand => (
                    <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="quantity">Quantity</Label>
              <Input id="quantity" type="number" value={newProduct.quantity} onChange={(e) => setNewProduct({ ...newProduct, quantity: parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} />
            </div>
            <div>
              <Label htmlFor="mrp">MRP</Label>
              <Input id="mrp" type="number" value={newProduct.mrp} onChange={(e) => setNewProduct({ ...newProduct, mrp: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} />
            </div>
            <div>
              <Label htmlFor="cost-price">Cost Price</Label>
              <Input id="cost-price" type="number" value={newProduct.costPrice} onChange={(e) => setNewProduct({ ...newProduct, costPrice: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} />
            </div>
            <div>
              <Label htmlFor="buy-date">Buy Date</Label>
              <Input id="buy-date" type="date" value={newProduct.buyDate} onChange={(e) => setNewProduct({ ...newProduct, buyDate: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="payment-date">Payment Date</Label>
              <Input id="payment-date" type="date" value={newProduct.paymentDate} onChange={(e) => setNewProduct({ ...newProduct, paymentDate: e.target.value })} />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Label htmlFor="payment-status">Payment Status</Label>
              <Select value={newProduct.paymentStatus} onValueChange={(value) => setNewProduct({ ...newProduct, paymentStatus: value })}>
                <SelectTrigger id="payment-status"><SelectValue placeholder="Payment Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Payment Pending</SelectItem>
                  <SelectItem value="partial">Partially Paid</SelectItem>
                  <SelectItem value="paid">Fully Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
            <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90" onClick={handleAddProduct} disabled={loading}>
              {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Add Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
